import React from 'react';
import config from '../../../../config';
import { createSlug, resolveActivitiesSlug } from '../../../../util/urlHelpers';
import { IconSendL, IconEyeL, IconClockAlarmL, IconAnonymousL } from '../../../../icons';

import css from './SectionNewActivities.css';
import { checkIfListingIsAnonymous, checkIfUserIsAnonymous } from '../../../../util/anonym';
import { ConditionalWrapper } from '../../../../util/common';
import { trimDisplayNameLastWord } from '../../../../util/text';
import { ensureListing, ensureUser } from '../../../../util/data';

const { userTypeRider, anonymHorseownerName } = config;

const RIDER_GETS_INQUIRY = 'rider-gets-inquiry';
const OWNER_GETS_INQUIRY = 'owner-gets-inquiry';
const RIDER_VISITS_LISTING = 'rider-visits-listing';
const OWNER_VISITS_RIDER = 'owner-visits-rider';
const RIDER_CONFIRMED_AVAILABILITY = 'rider-confirmed-availability';
const OWNER_ACTIVATED_LISTING = 'owner-activated-listing';

const AnonymWrapper = ({ children }) => (
    <div className={css.anonymOverlay}>
        {children}
        <IconAnonymousL />
    </div>
);

const getConfig = ({ horseowner, rider, listing }) => {
    const {
        attributes: { profile: horseownerProfile },
    } = ensureUser(horseowner);
    const {
        attributes: {
            title: listingTitle,
            publicData: { mainListingImage, activities },
        },
    } = ensureListing(listing);
    const {
        attributes: { profileImage, profile: riderProfile = {} },
    } = ensureUser(rider);
    const { publicData: riderPublicData = {} } = riderProfile;
    const { hasDefaultAvatar, interest } = riderPublicData;

    const listingId = listing?.id?.uuid;
    const riderId = rider?.id?.uuid;

    const isAnonymousListing = checkIfListingIsAnonymous(listing);
    const isAnonymousRider = !hasDefaultAvatar && checkIfUserIsAnonymous(rider);

    const riderName = trimDisplayNameLastWord(riderProfile.displayName);
    const horseownerName = isAnonymousListing
        ? anonymHorseownerName
        : trimDisplayNameLastWord(horseownerProfile.displayName);

    const riderImageVariants = profileImage?.attributes?.variants || {};
    const riderProfileImage = (Object.values(riderImageVariants)[0] || {}).url;

    const riderImageSection = (
        <ConditionalWrapper
            condition={isAnonymousRider}
            wrapper={children => <AnonymWrapper>{children}</AnonymWrapper>}
        >
            {riderProfileImage ? (
                <img className={css.image} src={riderProfileImage} />
            ) : (
                <div className={css.imagePlaceholder}>{(riderName || '').split('')[0]}</div>
            )}
        </ConditionalWrapper>
    );

    const listingImageSection = (
        <ConditionalWrapper
            condition={isAnonymousListing}
            wrapper={children => <AnonymWrapper>{children}</AnonymWrapper>}
        >
            {mainListingImage ? (
                <img className={css.image} src={mainListingImage} />
            ) : (
                <div className={css.imagePlaceholder}>{(listingTitle || '').split('')[0]}</div>
            )}
        </ConditionalWrapper>
    );

    const riderLinkProps = riderId
        ? {
              link: {
                  name: 'ProfilePage',
                  params: { id: riderId, userType: userTypeRider },
              },
          }
        : {};

    const listingLinkProps = listingId
        ? {
              link: {
                  name: 'ListingPage',
                  params: { id: listingId, slug: createSlug(listing) },
              },
          }
        : {};

    return {
        [RIDER_GETS_INQUIRY]: {
            publicImage: riderImageSection,
            icon: <IconSendL />,
            isAnonymousRider,
            description: (
                <p>
                    <b>{riderName}</b> hat von <b>{horseownerName}</b> eine Anfrage zu{' '}
                    <b>{listingTitle}</b> erhalten.
                </p>
            ),
            ...riderLinkProps,
        },
        [OWNER_GETS_INQUIRY]: {
            publicImage: listingImageSection,
            isAnonymousListing,
            icon: <IconSendL />,
            description: (
                <p>
                    <b>{horseownerName}</b> hat von <b>{riderName}</b> eine Anfrage zu{' '}
                    <b>{listingTitle}</b> erhalten.
                </p>
            ),
            ...listingLinkProps,
        },
        //Benjamin hat sich das Pferd Kinga angesehen.
        [RIDER_VISITS_LISTING]: {
            publicImage: riderImageSection,
            isAnonymousRider,
            icon: <IconEyeL />,
            description: (
                <p>
                    <b>{riderName}</b> hat sich das Pferd <b>{listingTitle}</b> angesehen.
                </p>
            ),
            ...riderLinkProps,
        },
        // Andrea hat zu Kinga das Profil von Benjamin angesehen.
        [OWNER_VISITS_RIDER]: {
            publicImage: listingImageSection,
            icon: <IconEyeL />,
            isAnonymousListing,
            description: (
                <p>
                    <b>{horseownerName}</b> hat zu <b>{listingTitle}</b> das Profil von{' '}
                    <b>{riderName}</b> angesehen.
                </p>
            ),
            ...listingLinkProps,
        },
        // Benjamin ist sofort verfügbar für eine Reitbeteiligung.
        [RIDER_CONFIRMED_AVAILABILITY]: {
            publicImage: riderImageSection,
            icon: <IconClockAlarmL />,
            isAnonymousRider,
            description: (
                <p>
                    <b>{riderName}</b> ist sofort verfügbar für eine{' '}
                    <span className={css.activityType}>
                        {interest && interest.length > 0
                            ? resolveActivitiesSlug(interest)
                            : 'Reitbeteiligung'}
                    </span>
                    .
                </p>
            ),
            ...riderLinkProps,
        },
        // Andrea sucht jetzt nach einer Reitbeteiligung für Kinga.
        [OWNER_ACTIVATED_LISTING]: {
            publicImage: listingImageSection,
            icon: <IconClockAlarmL />,
            isAnonymousListing,
            description: (
                <p>
                    <b>{horseownerName}</b> sucht jetzt nach einer{' '}
                    <span className={css.activityType}>
                        {resolveActivitiesSlug(activities) || 'Reitbeteiligung'}
                    </span>{' '}
                    für <b>{listingTitle}</b>.
                </p>
            ),
            ...listingLinkProps,
        },
    };
};

export const getContent = activity => {
    const { type } = activity;

    return getConfig(activity)[type] || {};
};
